/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useEffect, useState } from "react";
import debounce from "lodash/debounce";
import { TableRowValueDict } from "src/requests/tableRowActions/types";
import { SOR, TemplateId } from "../../../../globalTypes";
import { DEFAULT_TABLE_DATA } from "../../../organisms/TemplateView/fragments/TemplateTable/constants";
import { fetchTemplateTable } from "../../../../requests/table/requestCreators";
import flowioast from "../../../../utils/flowioast";

const useRelationTableData = (
  templateId: TemplateId,
  sor: SOR,
  searchTextValue: string,
  rowValues?: TableRowValueDict,
  detailTemplateId?: TemplateId | null
) => {
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [fullTextKeyword, setFullTextKeyword] = useState(searchTextValue || "");
  const [searchText, setSearchText] = useState(searchTextValue || "");
  const [currentPage, setCurrentPage] = useState(1);
  const [tableData, setTableData] = useState(DEFAULT_TABLE_DATA);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMoreMobile, setIsLoadingMoreMobile] = useState(false);
  const [isFetching, setIsFetching] = useState(false);

  const fetchTableData = () => {
    setIsFetching(true);
    fetchTemplateTable({
      templateId,
      sor,
      rowsPerPage,
      pageNumber: currentPage,
      fulltextQuery: fullTextKeyword,
      rowValues,
      detailTemplateId,
    })
      .then(({ data }) => setTableData(data))
      .catch((e) => {
        flowioast.error.fromIntlBase("relation_table.fetch.singular", e);
      })
      .finally(() => {
        setIsLoading(false);
        setIsLoadingMoreMobile(false);
        setIsFetching(false);
      });
  };

  useEffect(() => {
    setIsLoading(true);

    debounce(() => {
      if (!isFetching) fetchTableData();
    }, 500)();

    return () => {
      setIsLoading(false);
    };
  }, [fullTextKeyword, sor, currentPage, rowsPerPage]);

  const loadMoreMobile = () => {
    if (tableData.count < rowsPerPage) return;
    setRowsPerPage((old) => old + 10);
    setIsLoadingMoreMobile(true);
  };

  const debouncedSetFulltext = useCallback(
    debounce((newValue: string) => setFullTextKeyword(newValue), 500),
    [setFullTextKeyword]
  );

  const boundFullTextChange = (newValue: string) => {
    setSearchText(newValue);
    debouncedSetFulltext(newValue);
  };

  return {
    tableData,
    isLoading: isLoading && !isLoadingMoreMobile,
    loadMoreMobile,
    isLoadingMoreMobile,
    fetchTableData,
    rowsPerPage,
    currentPage,
    searchText,
    setRowsPerPage,
    setCurrentPage,
    updateFulltext: boundFullTextChange,
  };
};

export default useRelationTableData;
